import React, { useState } from 'react';
import { api } from '../services/api';

const LoginScreen = ({ onLogin }) => {
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async () => {
    // Clear previous errors
    setError('');

    // Validation
    if (!loginId || !password) {
      setError('Please enter both login ID and password');
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.auth.login(loginId, password);
      
      if (response.success && response.data) {
        // Map API user data to app user format
        const user = {
          ...response.data.user,
          role: response.data.user.role.toLowerCase(), // Convert to lowercase for consistency
          store: null, // Will be set from store assignment if needed
          name: response.data.user.fullName,
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
          expiresAt: response.data.expiresAt
        };
        
        onLogin(user);
      } else {
        setError(response.message || 'Login failed');
      }
    } catch (err) {
      console.error('Login error:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.status,
        statusText: err.statusText,
        data: err.data
      });
      
      // Show detailed error message
      let errorMsg = err.message || 'Invalid credentials or server error';
      if (err.status === 500) {
        errorMsg = `Server Error: ${err.message || 'The server encountered an error. Please check the server logs.'}`;
      } else if (err.status === 0) {
        errorMsg = 'Cannot connect to server. Please ensure the API server is running.';
      }
      
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isLoading) {
      handleLogin();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-jewel-red p-4">
      <div className="bg-white p-6 md:p-8 rounded-lg shadow-2xl w-full max-w-md">
        <div className="text-center mb-6">
          <div className="mb-4 flex justify-center">
            <img src="/logo.png" alt="OMG Gold & Diamond" className="h-16 md:h-20 object-contain" />
          </div>
          <p className="text-gray-500 text-sm md:text-base">Sign in to your account</p>
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm flex items-center">
              <i className="fas fa-exclamation-circle mr-2"></i>
              {error}
            </p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-xs md:text-sm font-semibold mb-1">Login ID</label>
            <input 
              type="text" 
              value={loginId}
              onChange={(e) => {
                setLoginId(e.target.value);
                setError('');
              }}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              className="w-full p-2 md:p-2.5 border rounded focus:outline-none focus:ring-2 focus:ring-red-500 text-sm md:text-base disabled:bg-gray-100" 
              placeholder="Enter your login ID"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs md:text-sm font-semibold mb-1">Password</label>
            <input 
              type="password" 
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              className="w-full p-2 md:p-2.5 border rounded focus:outline-none focus:ring-2 focus:ring-red-500 text-sm md:text-base disabled:bg-gray-100" 
              placeholder="Enter your password"
            />
          </div>
          <button 
            onClick={handleLogin}
            disabled={isLoading}
            className="w-full bg-jewel-red text-white py-2.5 md:py-3 rounded font-bold hover:bg-red-700 transition text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Logging in...
              </>
            ) : (
              <>
                <i className="fas fa-sign-in-alt"></i>
                LOGIN
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default LoginScreen;
